// Generado desde design-system/components/overlays/Modal.tsx — no editar; edita el .ts/.tsx y corre build-ds.
import { useId, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button, IconButton } from '../core/Button';
import { EXIT_MS, Portal, motionStyle, useDismiss, useFocusTrap, useInertOthers, usePresence, useReducedMotion, useScrollLock, } from './primitives';
const SIZES = { sm: 'max-w-[420px]', md: 'max-w-[540px]', lg: 'max-w-[760px]' };
const ICON_TONES = {
    accent: 'bg-accent-soft text-accent-ink',
    danger: 'bg-neg-soft text-neg',
    warn: 'bg-warn-soft text-warn',
    pos: 'bg-pos-soft text-pos',
};
/** Velo detrás de las capas modales (token --db-scrim: atenúa en claro y en oscuro). */
export function Scrim({ visible, reduced, onPointerDown, className }) {
    return (<div aria-hidden="true" onPointerDown={onPointerDown} className={cn('absolute inset-0 bg-scrim', className)} style={motionStyle('scrim', visible, reduced)}/>);
}
/**
 * Modal — diálogo modal sobre vidrio fuerte con velo.
 * role="dialog" (o "alertdialog"), aria-modal, foco atrapado, Esc y clic en el velo cierran,
 * el foco vuelve al disparador, el fondo queda `inert` y sin scroll.
 * inline: se monta dentro del contenedor `relative` más cercano (vistas previas, paneles embebidos).
 * onClose(reason) recibe 'escape' | 'scrim' | 'close'. Sin onClose el diálogo no se puede descartar.
 */
export function Modal({ open, onClose, title, description, icon: Icon, iconTone = 'accent', size = 'md', children, footer, inline = false, autoFocus = true, initialFocusRef, role = 'dialog', hideClose = false, closeOnScrim = true, className, bodyClassName, }) {
    const rootRef = useRef(null);
    const panelRef = useRef(null);
    const uid = useId();
    const titleId = `${uid}-t`;
    const descId = `${uid}-d`;
    const reduced = useReducedMotion();
    const { mounted, visible } = usePresence(open, EXIT_MS.dialog);
    const trap = useFocusTrap(panelRef, { active: open, autoFocus, initialFocusRef });
    useDismiss({ open, onClose, outside: false });
    useInertOthers(rootRef, open && mounted, inline ? 'inline' : 'portal');
    useScrollLock(open && !inline);
    if (!mounted)
        return null;
    const node = (<div ref={rootRef} data-db-overlay="" 
    // Al cerrar (animación de salida), la capa deja pasar los clics: la página responde de inmediato.
    className={cn(inline ? 'absolute' : 'fixed', 'inset-0 z-modal flex items-center justify-center p-4 sm:p-6', !visible && 'pointer-events-none')}>
      <Scrim visible={visible} reduced={reduced} onPointerDown={closeOnScrim && onClose ? () => onClose('scrim') : undefined}/>
      <div ref={panelRef} role={role} aria-modal="true" aria-labelledby={title ? titleId : undefined} aria-describedby={description ? descId : undefined} tabIndex={-1} onKeyDown={trap} className={cn('db-glass-strong relative flex max-h-full w-full flex-col overflow-hidden rounded-xl text-ink outline-none', SIZES[size] ?? SIZES.md, className)} style={motionStyle('dialog', visible, reduced)}>
        {(title || !hideClose) && (<header className="flex items-start gap-4 px-6 pb-3 pt-6">
            {Icon && (<span className={cn('grid h-11 w-11 shrink-0 place-items-center rounded-md', ICON_TONES[iconTone] ?? ICON_TONES.accent)}>
                <Icon size={21} strokeWidth={1.9} aria-hidden="true"/>
              </span>)}
            <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
              {title && (<h2 id={titleId} className="font-display text-title font-medium text-ink">
                  {title}
                </h2>)}
              {description && (<p id={descId} className="text-body text-ink-2">
                  {description}
                </p>)}
            </div>
            {!hideClose && onClose && (<IconButton icon={X} label="Cerrar" variant="ghost" size="sm" className="-mr-2 -mt-1" onClick={() => onClose('close')}/>)}
          </header>)}
        {children && <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2', bodyClassName)}>{children}</div>}
        {footer && (<footer className="flex flex-wrap items-center justify-end gap-3 border-t border-hair bg-[color-mix(in_srgb,var(--db-surface)_35%,transparent)] px-6 py-4">
            {footer}
          </footer>)}
      </div>
    </div>);
    return inline ? node : <Portal>{node}</Portal>;
}
/**
 * ConfirmDialog — confirmación de una acción destructiva (role="alertdialog").
 * El foco inicial va a "Cancelar" (la opción segura). `confirmText` exige escribir un texto
 * (p. ej. el nombre) antes de habilitar el botón destructivo. `consequences` lista qué se pierde.
 */
export function ConfirmDialog({ open, onClose, onConfirm, title, description, consequences, confirmLabel = 'Eliminar', cancelLabel = 'Cancelar', confirmText, loading = false, loadingText = 'Eliminando…', tone = 'danger', icon = AlertTriangle, inline = false, autoFocus = true, children, }) {
    const cancelRef = useRef(null);
    const [typed, setTyped] = useState('');
    const inputId = useId();
    const ready = !confirmText || typed.trim() === confirmText;
    const close = (why) => {
        if (loading)
            return;
        setTyped('');
        onClose?.(why);
    };
    return (<Modal open={open} onClose={close} role="alertdialog" size="sm" icon={icon} iconTone={tone === 'danger' ? 'danger' : 'warn'} title={title} description={description} inline={inline} autoFocus={autoFocus} initialFocusRef={cancelRef} hideClose closeOnScrim={!loading} footer={<>
          <Button ref={cancelRef} variant="secondary" onClick={() => close('cancel')} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={() => onConfirm?.()} disabled={!ready} loading={loading} loadingText={loadingText}>
            {confirmLabel}
          </Button>
        </>}>
      <div className="flex flex-col gap-4">
        {(consequences?.length ?? 0) > 0 && (<ul className="db-track flex flex-col gap-2 rounded-md p-4">
            {consequences?.map((c, i) => (<li key={i} className="flex gap-2.5 text-body-sm text-ink-2">
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-neg" aria-hidden="true"/>
                <span>{c}</span>
              </li>))}
          </ul>)}
        {children}
        {confirmText && (<div className="flex flex-col gap-2">
            <label htmlFor={inputId} className="text-body-sm font-semibold text-ink-2">
              Escribe <strong className="font-mono text-ink">{confirmText}</strong> para confirmar
            </label>
            <input id={inputId} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} className="db-sunken h-11 w-full rounded-sm px-4 font-mono text-body text-ink outline-none placeholder:text-ink-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" placeholder={confirmText}/>
          </div>)}
      </div>
    </Modal>);
}
